/**
 * Grove Fruit Slices - runtime contract.
 *
 * A Fruit Slice is a front-end artifact (pure HTML/CSS/JS) the client runs inside
 * a sandboxed `<iframe srcdoc>` (origin-null, no cookies, no network). Its only
 * door to data/storage is the `grove.*` bridge:
 *   - grove.call(capability, params) — read backend data (see capabilities.ts)
 *   - grove.load(scope) / grove.save(scope, data) — persist a little state
 *
 * All bridge calls run in the Grove client, scoped to the viewing user's JWT.
 * Backend-data calls are read-only; `llm.prompt` invokes an LLM; the
 * `document.*` calls are purely client-side file I/O. The slice never touches
 * credentials or the network directly.
 */

import type { FruitSliceFrame } from '@fruitful/types';

/* Wire protocol between the iframe (slice) and the host. */
export type BridgeRequest =
    | {
          __grove: 'request';
          id: number;
          method: 'call';
          capability: string;
          params?: unknown;
      }
    | { __grove: 'request'; id: number; method: 'load'; scope?: string }
    | {
          __grove: 'request';
          id: number;
          method: 'save';
          scope?: string;
          data?: unknown;
      };

export type BridgeResult =
    | { ok: true; result: unknown }
    | { ok: false; error: string };

/**
 * Injected into every slice to simplify emitted code.
 * Accessible via `await grove.save(...)` instead of hand-rolling postMessage.
 */
export const BRIDGE_SDK = `
(function () {
  var _id = 0;
  var pending = new Map();
  window.addEventListener('message', function (e) {
    var m = e.data;
    if (!m || m.__grove !== 'response') return;
    var p = pending.get(m.id);
    if (!p) return;
    pending.delete(m.id);
    m.ok ? p.resolve(m.result) : p.reject(new Error(m.error));
  });
  function send(payload) {
    return new Promise(function (resolve, reject) {
      var id = ++_id;
      pending.set(id, { resolve: resolve, reject: reject });
      parent.postMessage(Object.assign({ __grove: 'request', id: id }, payload), '*');
    });
  }
  window.grove = {
    call: function (capability, params) { return send({ method: 'call', capability: capability, params: params }); },
    load: function (scope) { return send({ method: 'load', scope: scope }); },
    save: function (scope, data) { return send({ method: 'save', scope: scope, data: data }); }
  };
})();
`;

/**
 * The CSS kit every slice gets for free. It mirrors the Smoothie design system
 * (grays/green/red palettes, spacing scale, radii, typography) so slices look
 * native to Grove without importing Tamagui. Design tokens are exposed as CSS
 * custom properties (`--g-*`) — a slice can reference them for one-off styling
 * instead of hard-coding hexes.
 */
export const GROVE_KIT = `
:root {
  --g-white: #ffffff;
  --g-bg: #ffffff;
  --g-surface: #f9f9f9;
  --g-surface-strong: #f5f5f5;
  --g-border: #e4e4e4;
  --g-border-strong: #dddddd;
  --g-text: #1f1f1f;
  --g-text-secondary: #5f5f5f;
  --g-text-muted: #7d7d7d;
  --g-positive: #00ab5f;
  --g-positive-strong: #007e3e;
  --g-positive-surface: #e1f4e7;
  --g-danger: #e23229;
  --g-danger-strong: #d01d19;
  --g-danger-surface: #fce7e4;
  --g-warning: #b25c00;
  --g-warning-surface: #fdeede;
  --g-accent: #1f1f1f;
  --g-radius-sm: 8px;
  --g-radius: 12px;
  --g-radius-lg: 16px;
  --g-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06);
  --g-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  --g-mono: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
}

* { box-sizing: border-box; }
body {
  margin: 0;
  font-family: var(--g-font);
  font-size: 14px;
  line-height: 1.5;
  color: var(--g-text);
  background: var(--g-bg);
  -webkit-font-smoothing: antialiased;
}
a { color: var(--g-positive-strong); text-decoration: none; }
a:hover { text-decoration: underline; }

/* Layout ------------------------------------------------------------------ */
.g-app { padding: 24px; max-width: 960px; margin: 0 auto; }
.g-header { margin-bottom: 20px; }
.g-header h1 { font-size: 20px; font-weight: 650; letter-spacing: -0.01em; margin: 0 0 4px; }
.g-sub { margin: 0; color: var(--g-text-secondary); font-size: 13px; }
.g-section { margin: 24px 0 0; }
.g-section-title { font-size: 12px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; color: var(--g-text-muted); margin: 0 0 10px; }
.g-divider { height: 1px; background: var(--g-border); border: 0; margin: 20px 0; }
.g-row { display: flex; align-items: center; gap: 12px; }
.g-row--between { justify-content: space-between; }
.g-stack { display: flex; flex-direction: column; gap: 12px; }
.g-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.g-grid--3 { grid-template-columns: repeat(3, 1fr); }
.g-grid--4 { grid-template-columns: repeat(4, 1fr); }
@media (max-width: 620px) { .g-grid, .g-grid--3, .g-grid--4 { grid-template-columns: 1fr; } }

/* Cards & stats ----------------------------------------------------------- */
.g-card { border: 1px solid var(--g-border); border-radius: var(--g-radius-lg); padding: 16px; background: var(--g-surface); }
.g-card--plain { background: var(--g-white); box-shadow: var(--g-shadow); }
.g-card-label { font-size: 12px; color: var(--g-text-muted); text-transform: uppercase; letter-spacing: .04em; }
.g-card-value { font-size: 28px; font-weight: 650; margin: 6px 0 4px; letter-spacing: -0.02em; }
.g-delta { font-size: 13px; font-weight: 600; }
.g-delta--up { color: var(--g-positive); }
.g-delta--down { color: var(--g-danger); }

/* Buttons ----------------------------------------------------------------- */
.g-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  font: inherit; font-weight: 600; font-size: 14px; line-height: 1;
  padding: 10px 16px; border-radius: 999px; border: 1px solid transparent;
  background: var(--g-accent); color: var(--g-white); cursor: pointer;
  transition: opacity .12s ease, background .12s ease;
}
.g-btn:hover { opacity: .9; }
.g-btn:active { opacity: .8; }
.g-btn:disabled { opacity: .4; cursor: not-allowed; }
.g-btn--secondary { background: var(--g-white); color: var(--g-text); border-color: var(--g-border-strong); }
.g-btn--secondary:hover { background: var(--g-surface-strong); opacity: 1; }
.g-btn--danger { background: var(--g-danger); }
.g-btn--sm { padding: 6px 12px; font-size: 13px; }

/* Badges ------------------------------------------------------------------ */
.g-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 600; line-height: 1;
  padding: 4px 10px; border-radius: 999px;
  background: var(--g-surface-strong); color: var(--g-text-secondary);
}
.g-badge--positive { background: var(--g-positive-surface); color: var(--g-positive-strong); }
.g-badge--danger { background: var(--g-danger-surface); color: var(--g-danger-strong); }
.g-badge--warning { background: var(--g-warning-surface); color: var(--g-warning); }

/* Forms ------------------------------------------------------------------- */
.g-field { display: flex; flex-direction: column; gap: 6px; }
.g-label { font-size: 12px; font-weight: 600; color: var(--g-text-secondary); }
.g-input, .g-select, .g-textarea {
  font: inherit; font-size: 14px; color: var(--g-text);
  padding: 10px 12px; border: 1px solid var(--g-border-strong); border-radius: var(--g-radius-sm);
  background: var(--g-white); width: 100%;
}
.g-input:focus, .g-select:focus, .g-textarea:focus { outline: none; border-color: var(--g-positive); }
.g-textarea { min-height: 96px; resize: vertical; }

/* Tables ------------------------------------------------------------------ */
.g-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.g-table th {
  text-align: left; font-weight: 600; color: var(--g-text-muted);
  text-transform: uppercase; letter-spacing: .04em; font-size: 11px;
  padding: 8px 12px; border-bottom: 1px solid var(--g-border);
}
.g-table td { padding: 10px 12px; border-bottom: 1px solid var(--g-border); }
.g-table tr:last-child td { border-bottom: 0; }
.g-table tbody tr:hover { background: var(--g-surface); }

/* Lists ------------------------------------------------------------------- */
.g-list { list-style: none; margin: 0; padding: 0; border: 1px solid var(--g-border); border-radius: var(--g-radius); overflow: hidden; }
.g-list-item { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 16px; border-bottom: 1px solid var(--g-border); }
.g-list-item:last-child { border-bottom: 0; }

/* Links -------------------------------------------------------------------- */
/* Slice links are click handlers calling navigation.*, so they have no href
   and need their affordances back. */
.g-link {
  color: var(--g-positive-strong); cursor: pointer; text-decoration: none;
  background: none; border: 0; padding: 0; font: inherit;
}
.g-link:hover { text-decoration: underline; }

/* State & utility --------------------------------------------------------- */
.g-loading, .g-error, .g-empty { color: var(--g-text-muted); font-size: 14px; padding: 16px 0; }
.g-error { color: var(--g-danger); }
.g-muted { color: var(--g-text-muted); }
.g-mono { font-family: var(--g-mono); }
.g-spinner {
  display: inline-block; width: 16px; height: 16px; vertical-align: -3px;
  border: 2px solid var(--g-border-strong); border-top-color: var(--g-text-secondary);
  border-radius: 50%; animation: g-spin .6s linear infinite;
}
@keyframes g-spin { to { transform: rotate(360deg); } }
`;

/**
 * Content Security Policy for the slice document. This is the real egress guard.
 *
 * `sandbox="allow-scripts"` already strips cookies, same-origin, and storage, but
 * it does NOT block outbound network calls.
 * (eg. slices could still exfiltrate it via fetch/XHR/WebSocket/sendBeacon or an <img>/external <script>)
 *
 * `default-src 'none'` denies every fetch sink; we then re-allow only the two
 * inline channels the runtime needs
 *   - the style kit <style>
 *   - the bridge <script>
 *   - and data: URI images
 *
 * With no `connect-src`/`script-src` host:
 *   - fetch
 *   - XHR
 *   - WebSocket
 *   - sendBeacon
 *   - dynamic import
 *   - eval
 *   - external scripts
 *   - nested frames
 * all fail.
 *
 * Additionally, `base-uri`/`form-action 'none'` close the form/base exfil paths too.
 */
const SLICE_CSP =
    "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'";

/* Compose full iframe document for a slice: CSP-> kit -> SDK -> slice body. */
export function buildSrcDoc(frame: Pick<FruitSliceFrame, 'html'>): string {
    return (
        `<meta http-equiv="Content-Security-Policy" content="${SLICE_CSP}">` +
        `<style>${GROVE_KIT}</style>` +
        `<script>${BRIDGE_SDK}</script>${frame.html}`
    );
}
